/**
 * Cross-entity aggregator for the templates-agent "Publish" affordance.
 *
 * Combines `useCurrentWorkspaceId` (entities/workspace) with the
 * `template-publish-workflow` entity SDK wrappers to produce everything the
 * widget needs: whether to show the button (are any pipelines registered?), the
 * in-flight state, a `publish` action that fans the artifact out to all
 * registered pipelines, and a derived `outcome` the publish modal renders as
 * spinner / success / error feedback.
 *
 * FSD prohibits cross-entity value imports between sibling entity slices, so this
 * aggregator lives in `shared/hooks/` — the `shared/hooks/usePublishTemplate/`
 * directory is classified under the `shared-cross-entity-aggregator` element
 * type in `eslint.config.js`, which grants it `shared → entities` value imports.
 *
 * Mirrors the pattern established by `useCustomWorkflows`.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  listTemplatePublishWorkflows,
  publishTemplate,
} from '@/entities/template-publish-workflow';
import { useCurrentWorkspaceId } from '@/entities/workspace';

interface PublishArtifact {
  html: string;
  subjectLine: string;
  preheaderText: string;
  /** Name for the published asset (falls back to this when the brief has no Brief_Name). */
  assetName: string;
  /** KB file id of the ".campaign" brief used to create this email, when one was selected. */
  campaignId?: string;
}

/** Derived, modal-friendly state of the publish request. */
export type PublishOutcome =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; published: number; warnings: string[] }
  | { kind: 'error'; messages: string[] };

export interface UsePublishTemplateResult {
  /** Whether the Publish button should be shown (≥1 pipeline registered). */
  showPublish: boolean;
  /** Whether a publish request is currently in flight. */
  isPublishing: boolean;
  /** Fans the artifact out to every registered pipeline for the workspace + agent. */
  publish: (artifact: PublishArtifact) => void;
  /** Current publish feedback the modal renders (idle/pending/success/error). */
  outcome: PublishOutcome;
  /** Clears the publish state (call when the modal closes / before re-publishing). */
  resetPublish: () => void;
}

export function usePublishTemplate(agentId: string, enabled = true): UsePublishTemplateResult {
  const { t } = useTranslation('common');
  const workspaceId = useCurrentWorkspaceId();

  const { data } = useQuery({
    enabled: enabled && !!workspaceId && !!agentId,
    queryKey: ['template-publish-workflows', workspaceId, agentId],
    queryFn: () => listTemplatePublishWorkflows(workspaceId!, agentId),
  });

  const mutation = useMutation({
    mutationFn: (artifact: PublishArtifact) =>
      publishTemplate({ workspaceId: workspaceId!, agentId, ...artifact }),
  });

  // Derive a single outcome the modal renders. A 2xx response can still be a logical failure
  // (some pipelines failed) — surface those per-pipeline errors, not just transport errors.
  const outcome: PublishOutcome = (() => {
    if (mutation.isPending) return { kind: 'pending' };
    if (mutation.isError)
      return {
        kind: 'error',
        messages: [t('briefTemplate.mutations.failedToPublishTemplate')],
      };
    if (mutation.isSuccess) {
      const result = mutation.data;
      if (result.totalFailed === 0) {
        // Non-fatal warnings from every completed pipeline (e.g. SFMC link/naming publish warnings).
        const warnings = result.results.flatMap((r) => r.warnings ?? []);
        return { kind: 'success', published: result.totalPublished, warnings };
      }
      const messages = result.results
        .filter((r) => r.status === 'failed')
        .map((r) => `${r.name}: ${r.error ?? t('components.sidePanel.unknownError')}`);
      return { kind: 'error', messages };
    }
    return { kind: 'idle' };
  })();

  // Guard against firing without a resolved workspace (the mutationFn asserts workspaceId).
  const publish = (artifact: PublishArtifact) => {
    if (!workspaceId) return;
    mutation.mutate(artifact);
  };

  return {
    showPublish: (data?.data.length ?? 0) > 0,
    isPublishing: mutation.isPending,
    publish,
    outcome,
    resetPublish: mutation.reset,
  };
}
